/**
 * NOTIF-003 — what happens in the browser when a new notification arrives: a quiet in-app card
 * in the shared toast stack (per the user's toast mode), one sound (when enabled) and, for a
 * hidden tab, an in-tab browser notification (when enabled and permitted); a burst collapses
 * into one "N new notifications". Pure decisions are exported for the tests; the effects never
 * throw — a blocked AudioContext or a denied permission is not an error.
 *
 * NOTIF-011 — a silent row (`data.silent`: an export being prepared) makes no sound and raises
 * no browser notification; a row with `data.toast = 'always'` shows its card whatever the
 * toast mode says (it answers the person's own click).
 */
import { h, type VNode } from 'vue'
import { toast } from '@/services/toastService'
import type { NotificationItem, NotificationSettings } from '@/services/notificationsApi'
import NotificationToastCard from '@/components/notifications/NotificationToastCard.vue'
import { alwaysToasts, isSilent } from '@/lib/notifications/exports'
import { LEVEL_LABELS } from '@/lib/notifications/pages'

/** Decision 6: toast for everything, only action_needed (default) or nothing. */
export function shouldToast(
  level: NotificationItem['level'],
  mode: NotificationSettings['toast_mode']
): boolean {
  if (mode === 'none') return false
  if (mode === 'all') return true
  return level === 'action_needed'
}

/** NOTIF-011 — the card shows when the mode allows the level, or when the row says "always". */
export function wantsToast(
  item: NotificationItem,
  mode: NotificationSettings['toast_mode']
): boolean {
  return alwaysToasts(item) || shouldToast(item.level, mode)
}

/**
 * NOTIF-004b: the sound mode reads like the toast mode but is judged on the whole batch —
 * one chime when any new row qualifies — and independently of what is shown. A silent row
 * (NOTIF-011) never qualifies.
 */
export function shouldSound(
  items: NotificationItem[],
  mode: NotificationSettings['sound_mode']
): boolean {
  return items.some((item) => !isSilent(item) && shouldToast(item.level, mode))
}

/** Browser notifications only make sense when the user is not looking at the app. */
export function shouldNotifyBrowser(settings: NotificationSettings, hidden: boolean): boolean {
  return settings.browser_enabled && hidden && browserNotificationsPermitted()
}

export function browserNotificationsSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.Notification === 'function'
}

export function browserNotificationsPermitted(): boolean {
  return browserNotificationsSupported() && window.Notification.permission === 'granted'
}

/**
 * Ask once, from a user gesture (the bell click). Resolves to the resulting permission;
 * 'denied' when the API is missing.
 */
export async function requestBrowserPermission(): Promise<NotificationPermission> {
  if (!browserNotificationsSupported()) return 'denied'
  if (window.Notification.permission !== 'default') return window.Notification.permission
  try {
    return await window.Notification.requestPermission()
  } catch {
    return 'denied'
  }
}

// ---------------------------------------------------------------- sound

let audioContext: AudioContext | null = null
let gestureHookInstalled = false

/**
 * Browsers only let audio start after a user gesture; create the context on the first
 * pointer / key event so a later poll-triggered ding is allowed to play.
 */
export function installSoundGestureHook(doc: Document = document): void {
  if (gestureHookInstalled) return
  gestureHookInstalled = true
  const wake = () => {
    try {
      audioContext ??= new AudioContext()
      if (audioContext.state === 'suspended') void audioContext.resume()
    } catch {
      /* no Web Audio here — stay silent */
    }
  }
  doc.addEventListener('pointerdown', wake, { passive: true })
  doc.addEventListener('keydown', wake, { passive: true })
}

/** One short two-tone ding synthesised in place (no asset to load). Never throws. */
export function playNotificationSound(): boolean {
  try {
    if (typeof AudioContext !== 'function') return false
    audioContext ??= new AudioContext()
    if (audioContext.state === 'suspended') return false
    const ctx = audioContext
    const gain = ctx.createGain()
    gain.gain.setValueAtTime(0.0001, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45)
    gain.connect(ctx.destination)
    for (const [freq, at] of [
      [880, 0],
      [1174.66, 0.12]
    ] as const) {
      const osc = ctx.createOscillator()
      osc.type = 'sine'
      osc.frequency.setValueAtTime(freq, ctx.currentTime + at)
      osc.connect(gain)
      osc.start(ctx.currentTime + at)
      osc.stop(ctx.currentTime + 0.5)
    }
    return true
  } catch {
    return false
  }
}

// ---------------------------------------------------------------- effects

export interface AnnounceContext {
  settings: NotificationSettings
  /** document.hidden at the time of the poll */
  hidden: boolean
  /** Opens the notification's deep link (router push) — used by toast / browser clicks. */
  open: (item: NotificationItem) => void
  /** `items` is a window of the newest rows and more may have arrived (burst label gets a "+"). */
  truncated?: boolean
}

/** The `tag` of the one browser notification a burst collapses into (a newer burst replaces it). */
export const BURST_NOTIFICATION_TAG = 'rukh-notifications-burst'

/** Opens the bell panel; set by the bell so a card click lands in it. */
let panelOpener: () => void = () => {}
/** Whether the bell panel is open right now; set by the bell. No card shows on top of it. */
let panelOpen: () => boolean = () => false

export function setNotificationPanelOpener(opener: () => void): void {
  panelOpener = opener
}

export function setNotificationPanelState(isOpen: () => boolean): void {
  panelOpen = isOpen
}

export function notificationPanelOpen(): boolean {
  return panelOpen()
}

/** The bell panel is opening: every notification card goes (they would sit on top of it). */
export function dismissNotificationToasts(): void {
  try {
    toast.dismissVariant('notification')
  } catch {
    /* no container registered — nothing to dismiss */
  }
}

/** How long a notification toast stays: action_needed longer, the rest shorter. */
export function toastTimeout(level: NotificationItem['level']): number {
  return level === 'action_needed' ? 10000 : 6000
}

/** The in-app notification card goes through the shared toast stack as a `notification` toast. */
/** Shows a card; returns its toast id ('' when there is no container to show it in). */
function showNotificationCard(render: () => VNode, timeout: number, ariaLabel: string): string {
  try {
    const id = toast.custom(
      { render },
      {
        variant: 'notification',
        showIcon: false,
        showCloseButton: true,
        showProgress: true,
        timeout,
        ariaLabel
      }
    )
    return typeof id === 'string' ? id : ''
  } catch {
    /* a missing toast container only warns */
    return ''
  }
}

function toastLine(item: NotificationItem): string {
  return item.body ? item.body : LEVEL_LABELS[item.level]
}

function burstLine(count: number, truncated: boolean): string {
  return `${count}${truncated ? '+' : ''} new notifications`
}

/**
 * One notification as a quiet card. The card is the action: Action needed → its deep link
 * (read once the page is open); Heads-up / Info → the bell panel. Nothing is read from a card
 * itself — a Heads-up / Info is read by resting the cursor on its row in the panel, not on the
 * card. The × only closes.
 */
/**
 * C2 (owner, 2026-11-02) — the card each notification is showing right now, by the row's id:
 * a row rewritten in place (an export's "being prepared…" turning "ready") replaces its earlier
 * card instead of stacking under it — the file was sometimes ready before the first card had
 * gone, and both sat on the screen.
 */
const cardByNotification = new Map<string, string>()
const CARDS_REMEMBERED = 50

export function showToast(item: NotificationItem, open: AnnounceContext['open']): void {
  const previous = cardByNotification.get(item.id)
  if (previous) {
    cardByNotification.delete(item.id)
    try {
      toast.dismiss(previous)
    } catch {
      /* the card may be gone already */
    }
  }
  const onActivate = item.level === 'action_needed' ? () => open(item) : () => panelOpener()
  let id = ''
  id = showNotificationCard(
    () => h(NotificationToastCard, { item, onActivate, onDismiss: () => dismissCard(item.id, id) }),
    toastTimeout(item.level),
    `${LEVEL_LABELS[item.level]}: ${item.title}`
  )
  if (id) {
    cardByNotification.set(item.id, id)
    while (cardByNotification.size > CARDS_REMEMBERED) {
      const oldest = cardByNotification.keys().next().value
      if (oldest === undefined) break
      cardByNotification.delete(oldest)
    }
  }
}

/** A card goes by itself (a ready export's file went to the browser): that card, no newer one. */
function dismissCard(notificationId: string, cardId: string): void {
  if (!cardId) return
  if (cardByNotification.get(notificationId) === cardId) cardByNotification.delete(notificationId)
  try {
    toast.dismiss(cardId)
  } catch {
    /* the card may be gone already */
  }
}

export function showBrowserNotification(
  item: NotificationItem,
  open: AnnounceContext['open']
): boolean {
  if (!browserNotificationsPermitted()) return false
  try {
    const n = new window.Notification(item.title, {
      body: toastLine(item),
      tag: item.id,
      silent: true
    })
    n.onclick = () => {
      try {
        window.focus()
        open(item)
        n.close()
      } catch {
        /* ignore */
      }
    }
    return true
  } catch {
    return false
  }
}

/** A burst (more rows than `limit`) collapses into one card: "N new notifications" → the panel. */
export function showBurstToast(count: number, actionNeeded: boolean, truncated = false): void {
  showNotificationCard(
    () => h(NotificationToastCard, { count, truncated, onActivate: () => panelOpener() }),
    actionNeeded ? 10000 : 6000,
    burstLine(count, truncated)
  )
}

/** The burst's browser notification: no single deep link, a click just brings the tab back. */
export function showBurstBrowserNotification(count: number, truncated = false): boolean {
  if (!browserNotificationsPermitted()) return false
  try {
    const n = new window.Notification('Notifications', {
      body: burstLine(count, truncated),
      tag: BURST_NOTIFICATION_TAG,
      silent: true
    })
    n.onclick = () => {
      try {
        window.focus()
        n.close()
      } catch {
        /* ignore */
      }
    }
    return true
  } catch {
    return false
  }
}

export interface AnnounceResult {
  toasts: number
  browser: number
  sound: boolean
  /** True when a burst was collapsed into one toast / one browser notification. */
  burst: boolean
}

/**
 * Announce newly arrived notifications (newest first): up to `limit` individual toasts —
 * chosen among the rows the toast mode allows, so an info row never crowds out an
 * action_needed one — and up to `limit` browser notifications; more than `limit` of either
 * collapses into a single "N new notifications". No toast at all while the bell panel is
 * open: the panel lists the new rows itself and a card would only cover it. One sound for
 * the batch when the sound mode allows it. Silent rows (NOTIF-011) get neither a sound nor a
 * browser notification — a card only. Returns what was done, for the tests.
 */
export function announceNew(
  items: NotificationItem[],
  ctx: AnnounceContext,
  limit = 3
): AnnounceResult {
  const result: AnnounceResult = { toasts: 0, browser: 0, sound: false, burst: false }
  if (items.length === 0) return result
  const truncated = ctx.truncated === true

  const toastable = panelOpen()
    ? []
    : items.filter((item) => wantsToast(item, ctx.settings.toast_mode))
  if (toastable.length > limit) {
    showBurstToast(
      toastable.length,
      toastable.some((item) => item.level === 'action_needed'),
      truncated
    )
    result.toasts = 1
    result.burst = true
  } else {
    for (const item of toastable) {
      showToast(item, ctx.open)
      result.toasts++
    }
  }

  const audible = items.filter((item) => !isSilent(item))
  if (audible.length > 0 && shouldNotifyBrowser(ctx.settings, ctx.hidden)) {
    if (audible.length > limit) {
      if (showBurstBrowserNotification(audible.length, truncated)) {
        result.browser = 1
        result.burst = true
      }
    } else {
      for (const item of audible) {
        if (showBrowserNotification(item, ctx.open)) result.browser++
      }
    }
  }

  if (shouldSound(items, ctx.settings.sound_mode)) {
    result.sound = playNotificationSound()
  }
  return result
}

/** Test seam: forget the audio context / gesture hook between tests. */
export function resetAlertsForTests(): void {
  audioContext = null
  gestureHookInstalled = false
  panelOpener = () => {}
  panelOpen = () => false
}
